import { useState } from "react";
import { Link, useLocation } from "wouter";
import { cn } from "@/lib/utils";

const sections = [
    {
        title: "Getting Started",
        items: [
            { slug: "quickstart", title: "Quickstart" },
            { slug: "authentication", title: "Authentication" },
            { slug: "rate-limits", title: "Rate Limits" },
        ],
    },
    {
        title: "Endpoints",
        items: [
            { slug: "current-weather", title: "Current Weather" },
            { slug: "forecast", title: "Forecast" },
            { slug: "historical", title: "Historical Data" },
            { slug: "alerts", title: "Weather Alerts" },
            { slug: "air-quality", title: "Air Quality" },
        ],
    },
    {
        title: "SDKs",
        items: [
            { slug: "typescript-sdk", title: "TypeScript" },
            { slug: "python-sdk", title: "Python" },
        ],
    },
];

export function DocsPage() {
    const [location] = useLocation();
    const [searchQuery, setSearchQuery] = useState("");

    // Get current doc from URL or default to quickstart
    const currentSlug = location.replace("/docs/", "") || "quickstart";

    return (
        <div className="min-h-screen bg-white">
            {/* Navbar */}
            <nav className="sticky top-0 bg-white/95 backdrop-blur border-b border-slate-200 z-50">
                <div className="max-w-7xl mx-auto px-6 py-3 flex justify-between items-center">
                    <Link href="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-gradient-to-br from-sky-400 to-sky-600 rounded-lg flex items-center justify-center text-sm shadow-lg shadow-sky-200">
                            ⛅
                        </div>
                        <span className="font-bold text-slate-800">
                            weatherapi<span className="text-sky-600">.dev</span>
                        </span>
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                            Docs
                        </span>
                    </Link>
                    <div className="flex items-center gap-4">
                        <input
                            type="search"
                            placeholder="Search docs..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-64 px-3 py-1.5 bg-slate-100 border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                        />
                        <Link
                            href="/dashboard"
                            className="px-4 py-1.5 bg-gradient-to-r from-sky-500 to-sky-600 text-white text-sm font-medium rounded-lg"
                        >
                            Dashboard
                        </Link>
                    </div>
                </div>
            </nav>

            <div className="max-w-7xl mx-auto flex">
                {/* Sidebar */}
                <aside className="w-64 shrink-0 border-r border-slate-200 h-[calc(100vh-57px)] sticky top-[57px] overflow-auto py-6 px-4">
                    {sections.map((section) => (
                        <div key={section.title} className="mb-6">
                            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">
                                {section.title}
                            </p>
                            <ul className="space-y-1">
                                {section.items.map((item) => (
                                    <li key={item.slug}>
                                        <Link
                                            href={`/docs/${item.slug}`}
                                            className={cn(
                                                "block px-2 py-1.5 text-sm rounded-lg transition-colors",
                                                currentSlug === item.slug
                                                    ? "bg-sky-50 text-sky-700 font-medium"
                                                    : "text-slate-600 hover:bg-slate-50"
                                            )}
                                        >
                                            {item.title}
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </aside>

                {/* Content */}
                <main className="flex-1 py-10 px-12 max-w-4xl">
                    {/* Quickstart content */}
                    {currentSlug === "quickstart" && (
                        <>
                            <h1 className="text-3xl font-bold text-slate-800 mb-4">Quickstart</h1>
                            <p className="text-lg text-slate-600 mb-8">
                                Get up and running with weatherapi.dev in under 5 minutes.
                            </p>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3 mt-8">
                                1. Get your API key
                            </h2>
                            <p className="text-slate-600 mb-4">
                                Sign up for a free account and get your API key from the{" "}
                                <Link href="/dashboard/keys" className="text-sky-600 hover:underline">
                                    dashboard
                                </Link>
                                .
                            </p>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3 mt-8">
                                2. Make your first request
                            </h2>
                            <div className="bg-slate-900 rounded-xl p-4 mb-4 overflow-x-auto">
                                <pre className="text-sm text-slate-100">
                                    <code>{`curl "https://api.weatherapi.dev/now?lat=37.77&lon=-122.42" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}</code>
                                </pre>
                            </div>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3 mt-8">
                                3. Parse the response
                            </h2>
                            <div className="bg-slate-900 rounded-xl p-4 mb-4 overflow-x-auto">
                                <pre className="text-sm text-slate-100">
                                    <code>{`{
  "location": {
    "lat": 37.77,
    "lon": -122.42,
    "name": "San Francisco, CA",
    "timezone": "America/Los_Angeles"
  },
  "current": {
    "temperature": { "celsius": 15.2, "fahrenheit": 59.4 },
    "condition": { "text": "Partly cloudy", "icon": "⛅" },
    "humidity": 72,
    "wind": { "kph": 19.4, "mph": 12.1 }
  }
}`}</code>
                                </pre>
                            </div>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3 mt-8">
                                4. Install an SDK (optional)
                            </h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <p className="font-medium text-slate-800 mb-2">TypeScript / JavaScript</p>
                                    <code className="text-sm text-slate-600 bg-white px-2 py-1 rounded">
                                        npm install weatherapi-dev
                                    </code>
                                </div>
                                <div className="bg-slate-50 rounded-xl p-4 border border-slate-200">
                                    <p className="font-medium text-slate-800 mb-2">Python</p>
                                    <code className="text-sm text-slate-600 bg-white px-2 py-1 rounded">
                                        pip install weatherapi-dev
                                    </code>
                                </div>
                            </div>
                        </>
                    )}

                    {/* Authentication content */}
                    {currentSlug === "authentication" && (
                        <>
                            <h1 className="text-3xl font-bold text-slate-800 mb-4">Authentication</h1>
                            <p className="text-lg text-slate-600 mb-8">
                                Learn how to authenticate your API requests.
                            </p>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3">API Keys</h2>
                            <p className="text-slate-600 mb-4">
                                All API requests require authentication via an API key. Include your key in the{" "}
                                <code className="bg-slate-100 px-1 py-0.5 rounded">Authorization</code> header:
                            </p>
                            <div className="bg-slate-900 rounded-xl p-4 mb-6 overflow-x-auto">
                                <pre className="text-sm text-slate-100">
                                    <code>Authorization: Bearer sk_live_your_api_key</code>
                                </pre>
                            </div>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3 mt-8">
                                Live vs Test Keys
                            </h2>
                            <ul className="list-disc list-inside text-slate-600 space-y-2">
                                <li>
                                    <code className="bg-slate-100 px-1 py-0.5 rounded">sk_live_*</code> - Production keys,
                                    usage is billed
                                </li>
                                <li>
                                    <code className="bg-slate-100 px-1 py-0.5 rounded">sk_test_*</code> - Test keys, returns
                                    mock data, not billed
                                </li>
                            </ul>
                        </>
                    )}

                    {/* Current Weather content */}
                    {currentSlug === "current-weather" && (
                        <>
                            <h1 className="text-3xl font-bold text-slate-800 mb-4">Current Weather</h1>
                            <p className="text-lg text-slate-600 mb-8">
                                Get real-time weather conditions for any location.
                            </p>

                            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200 mb-6">
                                <code className="text-sky-600 font-medium">GET /now</code>
                            </div>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3">Parameters</h2>
                            <table className="w-full text-sm mb-6">
                                <thead>
                                    <tr className="border-b border-slate-200">
                                        <th className="text-left py-2 font-semibold text-slate-700">Parameter</th>
                                        <th className="text-left py-2 font-semibold text-slate-700">Type</th>
                                        <th className="text-left py-2 font-semibold text-slate-700">Description</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-slate-100">
                                        <td className="py-2 font-mono text-slate-600">lat</td>
                                        <td className="py-2 text-slate-500">number</td>
                                        <td className="py-2 text-slate-600">Latitude (-90 to 90)</td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                        <td className="py-2 font-mono text-slate-600">lon</td>
                                        <td className="py-2 text-slate-500">number</td>
                                        <td className="py-2 text-slate-600">Longitude (-180 to 180)</td>
                                    </tr>
                                </tbody>
                            </table>

                            <h2 className="text-xl font-semibold text-slate-800 mb-3">Example</h2>
                            <div className="bg-slate-900 rounded-xl p-4 overflow-x-auto">
                                <pre className="text-sm text-slate-100">
                                    <code>{`curl "https://api.weatherapi.dev/now?lat=51.51&lon=-0.13" \\
  -H "Authorization: Bearer sk_live_xxx"`}</code>
                                </pre>
                            </div>
                        </>
                    )}

                    {/* Default for other pages */}
                    {!["quickstart", "authentication", "current-weather"].includes(currentSlug) && (
                        <>
                            <h1 className="text-3xl font-bold text-slate-800 mb-4">
                                {sections
                                    .flatMap((s) => s.items)
                                    .find((i) => i.slug === currentSlug)?.title || "Documentation"}
                            </h1>
                            <p className="text-lg text-slate-600 mb-8">Documentation coming soon...</p>
                            <div className="bg-sky-50 border border-sky-200 rounded-xl p-6">
                                <p className="text-sky-800">
                                    This page is under construction. Check back soon or{" "}
                                    <Link href="/dashboard" className="text-sky-600 hover:underline font-medium">
                                        try the API
                                    </Link>{" "}
                                    directly.
                                </p>
                            </div>
                        </>
                    )}
                </main>
            </div>
        </div>
    );
}

export default DocsPage;
